import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { managerView, overrideBonusMonth } from '../utils/commissions';
import { withSummerProgram } from '../utils/summerProgram';
import { roiMultiple } from '../utils/roiMultiple';
import { RowData, Inputs, PackageType, BuildPayType, LEADER_CAMPAIGN_MAX_MONTHS, LEADER_CAMPAIGN_MONTHLY_AD_SPEND, LEADER_CAMPAIGN_CPH, getLeaderAdSpend, Timeframe, getLeaderMonths, getRampShares, salesPerNinetyDayRep, getPricing, day15Share, day45Share, DOUBLE_ATTRITION_SURVIVAL, usesRevenueTrail } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CAMPAIGN_SPEND_STEP = 2500;
// In the 24-month view, months 4 and beyond are rolled up into quarters.
const QUARTERLY_FROM_MONTH = 4;
interface DisplayEntry {
  row: RowData;
  // Model months this column covers (empty for the Build column).
  months: number[];
  aggregate: boolean;
}

// Money and volume add up across a quarter; rep counts are shown as the
// quarter's average team size, and profit-to-date carries the last month.
// Quarter labels count months from signing (leader months included).
function aggregateRows(group: RowData[], months: number[], monthOffset = 0): RowData {
  const sum = (pick: (row: RowData) => number | undefined) => group.reduce((total, row) => total + (pick(row) ?? 0), 0);
  const avg = (pick: (row: RowData) => number | undefined) => sum(pick) / group.length;
  const last = group[group.length - 1];
  return {
    period: `Mo ${months[0] - monthOffset}-${months[months.length - 1] - monthOffset}`,
    activeCampaigns: avg(r => r.activeCampaigns),
    hires: sum(r => r.hires),
    launched: avg(r => r.launched),
    active30d: avg(r => r.active30d),
    active60d: avg(r => r.active60d),
    active90d: avg(r => r.active90d),
    veterans: avg(r => r.veterans),
    rampingReps: avg(r => r.rampingReps),
    rampingSales: sum(r => r.rampingSales),
    veteranReps: avg(r => r.veteranReps),
    buildFee: sum(r => r.buildFee),
    platformFee: sum(r => r.platformFee),
    campaignFee: sum(r => r.campaignFee),
    recruiterFee: sum(r => r.recruiterFee),
    leaderRevShare: sum(r => r.leaderRevShare),
    hireCost: sum(r => r.hireCost),
    adSpend: sum(r => r.adSpend),
    bonuses: sum(r => r.bonuses),
    totalCost: sum(r => r.totalCost),
    totalSales: sum(r => r.totalSales),
    realizationRate: last.realizationRate,
    grossRevenue: sum(r => r.grossRevenue),
    grossProfit: sum(r => r.grossProfit),
    netProfit: sum(r => r.netProfit),
    cumProfit: last.cumProfit,
    residualRevenue: sum(r => r.residualRevenue),
    totalResidualRevenue: sum(r => r.totalResidualRevenue),
    commissions: sum(r => r.commissions),
    commissionUpfront: sum(r => r.commissionUpfront),
    commissionResidual: sum(r => r.commissionResidual),
    commissionClawback: sum(r => r.commissionClawback),
    commissionBackend: sum(r => r.commissionBackend)
  };
}

// Build & implementation payment schedules per package. The first entry is the
// upfront payment in the Build column; remaining entries are consecutive
// monthly payments beginning in Month 2.
const BUILD_SPLIT_PLANS: Partial<Record<PackageType, number[][]>> = {
  advantage: [[1500, 1500, 1500]]
};
interface ProjectionTableProps {
  rows: RowData[];
  inputs: Inputs;
  onInputsChange: (inputs: Inputs) => void;
  // Scenario toggles live in the chart section; the table only reflects them.
  halfSales: boolean;
  doubleAttrition: boolean;
  onTarget: boolean;
  onToggleOnTarget: () => void;
  lowRetention: boolean;
  onToggleLowRetention: () => void;
  plusFive: boolean;
  onPlusFive: () => void;
  timeframe?: Timeframe;
  setTimeframe?: (t: Timeframe) => void;
}
export function ProjectionTable({
  rows,
  inputs,
  onInputsChange,
  halfSales,
  doubleAttrition,
  onTarget,
  onToggleOnTarget,
  lowRetention,
  onToggleLowRetention,
  plusFive,
  onPlusFive,
  timeframe: timeframeProp,
  setTimeframe: setTimeframeProp
}: ProjectionTableProps) {
  const [tooltip, setTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for Monthly Fees breakdown
  const [feePopup, setFeePopup] = useState<{
    rowIdx: number;
    platformFee: number;
    campaignFee: number;
    recruiterFee: number;
    x: number;
    y: number;
  } | null>(null);
  // Click popup for the milestone bonus breakdown
  const [bonusPopup, setBonusPopup] = useState<{
    rowIdx: number;
    day15Reps: number;
    day45Reps: number;
    day90Reps: number;
    day15: number;
    day45: number;
    day90: number;
    earnedPeriod: string;
    x: number;
    y: number;
  } | null>(null);
  // Month 2 sales are directly editable during the leader campaign
  const [editingLeaderSales, setEditingLeaderSales] = useState(false);
  const [editLeaderSalesValue, setEditLeaderSalesValue] = useState<string>('');
  // Click popup for the Total Sales explanation
  const [salesPopup, setSalesPopup] = useState<{
    rowIdx: number;
    period: string;
    totalSales: number;
    rampingReps: number;
    rampingSales: number;
    veterans: number;
    veteranSales: number;
    x: number;
    y: number;
  } | null>(null);
  // Track which ad spend cell is being overridden with a custom amount
  const [editingAdSpend, setEditingAdSpend] = useState<number | null>(null);
  const [editAdSpendValue, setEditAdSpendValue] = useState<string>('');
  // Track which campaign cell is being edited
  const [editingCampaign, setEditingCampaign] = useState<number | null>(null);
  const [editCampaignValue, setEditCampaignValue] = useState<string>('');
  // Track which fee cells are being edited
  const [editingBuildFee, setEditingBuildFee] = useState<number | null>(null);
  const [editBuildFeeValue, setEditBuildFeeValue] = useState<string>('');
  const [editingPlatformFee, setEditingPlatformFee] = useState<number | null>(null);
  const [editPlatformFeeValue, setEditPlatformFeeValue] = useState<string>('');
  const [editingCampaignFee, setEditingCampaignFee] = useState<number | null>(null);
  const [editCampaignFeeValue, setEditCampaignFeeValue] = useState<string>('');
  // Track which metric row label is being hovered
  const [hoveredMetricKey, setHoveredMetricKey] = useState<string | null>(null);
  // Track which data cell is being hovered (for crosshair highlight)
  const [hoveredCell, setHoveredCell] = useState<{
    metricKey: string;
    colIdx: number;
  } | null>(null);
  // Timeframe state (controlled if props provided)
  const [timeframeInternal, setTimeframeInternal] = useState<Timeframe>(6);
  const timeframe = timeframeProp ?? timeframeInternal;
  const setTimeframe = setTimeframeProp ?? setTimeframeInternal;
  // Revenue delay lives in the shared inputs so the proposal's Tailor panel
  // and the model pages follow the same shift.
  const revenueShift = Math.min(11, Math.max(0, Math.round(Number(inputs.revenueDelayMonths) || 0)));
  const setRevenueShift = (update: (prev: number) => number) => {
    const next = Math.min(11, Math.max(0, update(revenueShift)));
    if (next !== revenueShift) onInputsChange({
      ...inputs,
      revenueDelayMonths: next
    });
  };
  // Sales delay state (number of months to zero out at the start)
  const [salesDelay, setSalesDelay] = useState<number>(0);
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    recruiting: true,
    activeReps: true,
    investment: false,
    revenue: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const showTooltip = (content: string, e: React.MouseEvent) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setTooltip({
      content,
      x: rect.right + 8,
      y: rect.top
    });
  };
  const hideTooltip = () => {
    setTooltip(null);
  };
  // When the leader campaign runs, the standard schedule is shifted out by the
  // length of the search, so a displayed month maps back that many slots in the
  // input arrays.
  const leaderMonths = getLeaderMonths(inputs);
  const leaderOffset = leaderMonths;
  // Active fee schedule drives every fee amount and description below.
  const pricing = getPricing(inputs);
  const hasMilestoneBonuses = pricing.bonusDay45 > 0 || pricing.bonusDay90 > 0;
  const milestoneParts = [`${fmtMoney(pricing.bonusDay15)} at day 15`, pricing.bonusDay45 > 0 ? `${fmtMoney(pricing.bonusDay45)} at day 45` : null, pricing.bonusDay90 > 0 ? `${fmtMoney(pricing.bonusDay90)} at day 90` : null].filter(Boolean);
  const bonusFormula = hasMilestoneBonuses ? `${milestoneParts.join(' + ')} per active rep` : `One-time ${fmtMoney(pricing.bonusDay15)} placement bonus per rep at day 15 active in the field`;
  const bonusSummary = hasMilestoneBonuses ? `${milestoneParts.join(' + ')} per rep still active in the field` : `${fmtMoney(pricing.bonusDay15)} placement bonus at day 15`;
  // Ramping production, as a share of a veteran's monthly sales.
  const rampPcts = getRampShares(inputs).map(share => Math.round(share * 100));
  const ninetyDayRepSales = salesPerNinetyDayRep(inputs);
  const isLeaderMonth = (monthIndex: number) => leaderMonths > 0 && monthIndex >= 1 && monthIndex <= leaderMonths;
  const inputIndexForMonth = (monthIndex: number) => monthIndex - 1 - leaderOffset;
  // Handle monthly campaign overrides
  const handleCampaignChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    newMonthlyCampaigns[arrayIndex] = Math.max(0, value);
    // Editing the tier directly resets that month to the standard $2,500 per
    // campaign, clearing any custom spend override.
    newMonthlyAdSpend[arrayIndex] = CAMPAIGN_SPEND_STEP;
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const spendToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx >= arrayIndex ? valueToPropagate : val;
    });
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx >= arrayIndex ? spendToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const handlePushLeft = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    const valueToPropagate = currentMonthlyCampaigns[arrayIndex];
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const spendToPropagate = currentMonthlyAdSpend[arrayIndex];
    const newMonthlyCampaigns = currentMonthlyCampaigns.map((val, idx) => {
      return idx <= arrayIndex ? valueToPropagate : val;
    });
    const newMonthlyAdSpend = currentMonthlyAdSpend.map((val, idx) => {
      return idx <= arrayIndex ? spendToPropagate : val;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  // Total monthly ad spend is stored as (per-campaign spend × campaign tier).
  // Any custom total is allowed; the campaign tier — and therefore package
  // pricing — is derived from $2,500 bands: $1-2,500 = 1x, $2,501-5,000 = 2x,
  // $5,001-7,500 = 3x, and so on.
  const campaignTierForSpend = (total: number) => total <= 0 ? 0 : Math.ceil(total / CAMPAIGN_SPEND_STEP);
  const applyMonthlyTotals = (totalsByIndex: Record<number, number>) => {
    const newMonthlyAdSpend = [...(inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend))];
    const newMonthlyCampaigns = [...(inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns))];
    Object.entries(totalsByIndex).forEach(([key, rawTotal]) => {
      const arrayIndex = Number(key);
      const total = Math.max(0, Math.round(rawTotal));
      const tier = campaignTierForSpend(total);
      newMonthlyCampaigns[arrayIndex] = tier;
      newMonthlyAdSpend[arrayIndex] = tier === 0 ? 0 : total / tier;
    });
    onInputsChange({
      ...inputs,
      monthlyCampaigns: newMonthlyCampaigns,
      monthlyAdSpend: newMonthlyAdSpend
    });
  };
  const getMonthlyTotalSpend = (monthIndex: number) => {
    if (monthIndex === 0) return 0;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return 0;
    const currentMonthlyAdSpend = inputs.monthlyAdSpend || Array(12).fill(inputs.adSpend);
    const currentMonthlyCampaigns = inputs.monthlyCampaigns || Array(12).fill(inputs.campaigns);
    return currentMonthlyAdSpend[arrayIndex] * currentMonthlyCampaigns[arrayIndex];
  };
  // Summer program preset: ads paused Months 6–12, sales paused Months 1–3
  // and 10–12 (months counted from signing). Everything stays editable after;
  // turning it off only undoes the preset's own pauses.
  const toggleSummerProgram = () => onInputsChange(withSummerProgram(inputs, !inputs.summerProgram));
  const handleAdSpendChange = (monthIndex: number, value: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    applyMonthlyTotals({
      [arrayIndex]: Math.max(0, value)
    });
  };
  // Arrows always snap to the next / previous $2,500 tier boundary.
  const stepAdSpend = (monthIndex: number, direction: 1 | -1) => {
    if (monthIndex === 0) return;
    const total = getMonthlyTotalSpend(monthIndex);
    const nextTotal = direction === 1 ? (Math.floor(total / CAMPAIGN_SPEND_STEP) + 1) * CAMPAIGN_SPEND_STEP :
    // Stepping below the first tier pauses ads for the month ($0).
    Math.max(0, (Math.ceil(total / CAMPAIGN_SPEND_STEP) - 1) * CAMPAIGN_SPEND_STEP);
    handleAdSpendChange(monthIndex, nextTotal);
  };
  const handlePushAdSpendLeft = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const totalToPropagate = getMonthlyTotalSpend(monthIndex);
    const totals: Record<number, number> = {};
    for (let idx = 0; idx <= arrayIndex; idx++) {
      totals[idx] = totalToPropagate;
    }
    applyMonthlyTotals(totals);
  };
  const handlePushAdSpendRight = (monthIndex: number) => {
    if (monthIndex === 0) return;
    const arrayIndex = inputIndexForMonth(monthIndex);
    if (arrayIndex < 0 || arrayIndex > 11) return;
    const totalToPropagate = getMonthlyTotalSpend(monthIndex);
    const totals: Record<number, number> = {};
    for (let idx = arrayIndex; idx < 12; idx++) {
      totals[idx] = totalToPropagate;
    }
    applyMonthlyTotals(totals);
  };
  // Milestone bonus rep counts per month: day 15 lands in the launch month,
  // day 45 the following month, and day 90 three months after launch. Bonuses
  // are billed in arrears, so a billing month shows what was earned the month
  // before.
  const getBonusMilestones = (billingRowIdx: number) => {
    const rowIdx = billingRowIdx - 1;
    const rowAt = (idx: number) => idx >= 1 && idx < displayRows.length ? displayRows[idx] : undefined;
    const earnedPeriod = rowAt(rowIdx)?.period ?? '';
    const current = rowAt(rowIdx);
    const prior = rowAt(rowIdx - 1);
    const pct30 = inputs.pct30 / 100;
    const pct60 = Math.sqrt(inputs.pct90 / 100);
    // The leader is a single guaranteed rep hired in the last search month
    // (displayed month = leaderMonths). They hit each milestone outright, so
    // they're counted separately from the retention-weighted cohorts.
    const leaderAt = (month: number) => leaderMonths > 0 && month === leaderMonths ? 1 : 0;
    const leaderDay15 = leaderAt(rowIdx);
    const leaderDay45 = leaderAt(rowIdx - 1);
    const leaderDay90 = leaderAt(rowIdx - 3);
    // Each bonus counts the reps estimated to reach the milestone, rounded up
    // (day 15 and day 45 sit halfway between the retention rates around them).
    const day15Reps = (current ? Math.ceil(Math.max(0, current.launched - leaderDay15) * day15Share(pct30)) : 0) + leaderDay15;
    const day45Reps = (prior ? Math.ceil(Math.max(0, prior.launched - leaderAt(rowIdx - 1)) * day45Share(pct30, pct60)) : 0) + leaderDay45;
    const day90Reps = (current ? Math.round(Math.max(0, current.active90d - leaderDay90)) : 0) + leaderDay90;
    const enabled = !!inputs.pkg && inputs.salesSchoolEnabled !== false;
    return {
      day15Reps: enabled ? day15Reps : 0,
      day45Reps: enabled ? day45Reps : 0,
      day90Reps: enabled ? day90Reps : 0,
      day15: enabled ? day15Reps * pricing.bonusDay15 : 0,
      day45: enabled ? day45Reps * pricing.bonusDay45 : 0,
      day90: enabled ? day90Reps * pricing.bonusDay90 : 0,
      earnedPeriod
    };
  };
  // Sales pauses are stored by model month (row index − 1).
  const isSalesPaused = (rowIdx: number) => (inputs.pausedSalesMonths || []).includes(rowIdx - 1);
  const toggleSalesPause = (rowIdx: number) => {
    const month = rowIdx - 1;
    const current = inputs.pausedSalesMonths || [];
    onInputsChange({
      ...inputs,
      pausedSalesMonths: current.includes(month) ? current.filter(m => m !== month) : [...current, month].sort((a, b) => a - b)
    });
  };
  // The final search month is the leader rep's hire and first selling month.
  const isLeaderSalesMonth = (monthIndex: number) => leaderMonths > 0 && monthIndex === leaderMonths;
  const saveLeaderSalesEdit = () => {
    const parsed = parseFloat(editLeaderSalesValue);
    setEditingLeaderSales(false);
    setEditLeaderSalesValue('');
    if (!Number.isNaN(parsed)) {
      onInputsChange({
        ...inputs,
        leaderMonth2Sales: Math.max(0, parsed)
      });
    }
  };
  const handleLeaderSalesKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveLeaderSalesEdit();
    } else if (e.key === 'Escape') {
      setEditingLeaderSales(false);
      setEditLeaderSalesValue('');
    }
  };
  // Leader search months keep their own ad spend; the pipeline fee follows it
  // at the active schedule's rate per $2,500 bucket.
  const setLeaderAdSpend = (monthIndex: number, total: number) => {
    const next = Array.from({
      length: LEADER_CAMPAIGN_MAX_MONTHS
    }, (_, i) => getLeaderAdSpend(inputs, i));
    next[monthIndex - 1] = Math.max(0, total);
    onInputsChange({
      ...inputs,
      leaderAdSpend: next
    });
  };
  const startEditingAdSpend = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingAdSpend(monthIndex);
    setEditAdSpendValue(Math.round(currentValue).toString());
  };
  const saveAdSpendEdit = () => {
    if (editingAdSpend !== null) {
      const value = parseFloat(editAdSpendValue);
      if (!Number.isNaN(value)) {
        if (isLeaderMonth(editingAdSpend)) {
          setLeaderAdSpend(editingAdSpend, value);
        } else {
          handleAdSpendChange(editingAdSpend, value);
        }
      }
      setEditingAdSpend(null);
      setEditAdSpendValue('');
    }
  };
  const cancelAdSpendEdit = () => {
    setEditingAdSpend(null);
    setEditAdSpendValue('');
  };
  const handleAdSpendKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveAdSpendEdit();
    } else if (e.key === 'Escape') {
      cancelAdSpendEdit();
    }
  };
  const startEditingCampaign = (monthIndex: number, currentValue: number) => {
    if (monthIndex === 0) return;
    setEditingCampaign(monthIndex);
    setEditCampaignValue(currentValue.toString());
  };
  const saveCampaignEdit = () => {
    if (editingCampaign !== null) {
      const value = parseFloat(editCampaignValue) || 0;
      handleCampaignChange(editingCampaign, value);
      setEditingCampaign(null);
      setEditCampaignValue('');
    }
  };
  const cancelCampaignEdit = () => {
    setEditingCampaign(null);
    setEditCampaignValue('');
  };
  const handleCampaignKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignEdit();
    }
  };
  // Handle build fee editing - saves to inputs for persistence
  const startEditingBuildFee = (monthIndex: number, currentValue: number) => {
    setEditingBuildFee(monthIndex);
    setEditBuildFeeValue(currentValue.toString());
  };
  const handleBuildFeeChange = (monthIndex: number, value: number) => {
    // Initialize monthly build fees array if not present
    // Index 0 = Build, Index 1 = Month 1, etc. (13 total slots)
    const currentBuildFees = inputs.monthlyBuildFees || Array(13).fill(undefined);
    const newBuildFees = [...currentBuildFees];
    newBuildFees[monthIndex] = Math.max(0, value);
    onInputsChange({
      ...inputs,
      monthlyBuildFees: newBuildFees
    });
  };
  // Build & implementation payment plans. Index 0 is the Build column, the rest
  // are consecutive months. Clicking the row label cycles upfront → plans → upfront.
  const buildSplitPlans = BUILD_SPLIT_PLANS[inputs.pkg] ?? [];
  const buildFeeTotal = (() => {
    if (!inputs.pkg) return 0;
    return pricing.implementationFee + (leaderMonths > 0 ? pricing.leaderBuildUpcharge : 0) + (inputs.addRecruiter ? pricing.recruiterBuildUpcharge : 0);
  })();
  const activeBuildPlan = (() => {
    const fees = inputs.monthlyBuildFees;
    if (!fees) return 0;
    const paying = fees.filter(fee => fee !== undefined && fee !== null && fee > 0).length;
    const matched = buildSplitPlans.findIndex(plan => plan.length === paying);
    return matched === -1 ? 0 : matched + 1;
  })();
  const cycleBuildPlan = () => {
    if (buildSplitPlans.length === 0) return;
    const nextPlan = activeBuildPlan >= buildSplitPlans.length ? 0 : activeBuildPlan + 1;
    if (nextPlan === 0) {
      onInputsChange({
        ...inputs,
        buildPay: 'upfront',
        monthlyBuildFees: undefined
      });
      return;
    }
    // Payments are always equal: the full implementation fee (including any
    // leader or recruiter upcharges) is divided evenly across the plan.
    const plan = buildSplitPlans[nextPlan - 1];
    const payments = plan.length;
    const perPayment = buildFeeTotal / payments;
    const newBuildFees: (number | undefined)[] = Array(13).fill(undefined);
    for (let idx = 0; idx < payments; idx++) {
      // idx 0 is the upfront payment; monthly payments start in Month 2.
      const slot = idx === 0 ? 0 : idx + 1;
      newBuildFees[slot] = perPayment;
    }
    onInputsChange({
      ...inputs,
      buildPay: 'two',
      monthlyBuildFees: newBuildFees as number[]
    });
  };
  const saveBuildFeeEdit = () => {
    if (editingBuildFee !== null) {
      const value = parseFloat(editBuildFeeValue) || 0;
      handleBuildFeeChange(editingBuildFee, value);
      setEditingBuildFee(null);
      setEditBuildFeeValue('');
    }
  };
  const cancelBuildFeeEdit = () => {
    setEditingBuildFee(null);
    setEditBuildFeeValue('');
  };
  const handleBuildFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveBuildFeeEdit();
    } else if (e.key === 'Escape') {
      cancelBuildFeeEdit();
    }
  };
  const startEditingPlatformFee = (monthIndex: number, currentValue: number) => {
    setEditingPlatformFee(monthIndex);
    setEditPlatformFeeValue(currentValue.toString());
  };
  const savePlatformFeeEdit = () => {
    if (editingPlatformFee !== null) {
      setEditingPlatformFee(null);
      setEditPlatformFeeValue('');
    }
  };
  const cancelPlatformFeeEdit = () => {
    setEditingPlatformFee(null);
    setEditPlatformFeeValue('');
  };
  const handlePlatformFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      savePlatformFeeEdit();
    } else if (e.key === 'Escape') {
      cancelPlatformFeeEdit();
    }
  };
  const startEditingCampaignFee = (monthIndex: number, currentValue: number) => {
    setEditingCampaignFee(monthIndex);
    setEditCampaignFeeValue(currentValue.toString());
  };
  const saveCampaignFeeEdit = () => {
    if (editingCampaignFee !== null) {
      setEditingCampaignFee(null);
      setEditCampaignFeeValue('');
    }
  };
  const cancelCampaignFeeEdit = () => {
    setEditingCampaignFee(null);
    setEditCampaignFeeValue('');
  };
  const handleCampaignFeeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCampaignFeeEdit();
    } else if (e.key === 'Escape') {
      cancelCampaignFeeEdit();
    }
  };
  // Apply scenario adjustments to rows
  const adjustedRows = useMemo(() => {
    if (!halfSales && !doubleAttrition && salesDelay === 0) return rows;
    return rows.map((row, idx) => {
      let newRow = {
        ...row
      };
      // Apply sales delay (zero out sales for first N months)
      // Also shift residual revenue when sales are delayed
      if (salesDelay > 0 && idx > 0 && idx <= salesDelay) {
        // Zero out sales and residual for months 1 through salesDelay
        newRow.totalSales = 0;
        newRow.grossRevenue = 0;
        newRow.residualRevenue = 0;
        newRow.commissions = 0;
      } else if (salesDelay > 0 && idx > salesDelay) {
        // For months after the delay, shift the residual revenue
        // The residual revenue should come from (idx - salesDelay) position in original rows
        const sourceIdx = idx - salesDelay;
        if (sourceIdx > 0 && sourceIdx < rows.length) {
          newRow.residualRevenue = rows[sourceIdx].residualRevenue || 0;
        } else {
          newRow.residualRevenue = 0;
        }
      }
      // The revenue delay is already applied in the model itself.
      // Apply half sales scenario (commissions move with sales)
      if (halfSales) {
        newRow.totalSales = newRow.totalSales * 0.5;
        newRow.grossRevenue = newRow.grossRevenue * 0.5;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * 0.5;
        newRow.commissions = (newRow.commissions || 0) * 0.5;
      }
      // Apply double attrition (half retention) scenario
      if (doubleAttrition) {
        newRow.launched = row.launched * 0.5;
        newRow.active30d = row.active30d * 0.5;
        newRow.active60d = row.active60d * 0.5;
        newRow.active90d = row.active90d * 0.5;
        newRow.veterans = row.veterans * 0.5;
        const retentionMultiplier = 0.5;
        newRow.totalSales = newRow.totalSales * retentionMultiplier;
        newRow.grossRevenue = newRow.grossRevenue * retentionMultiplier;
        newRow.residualRevenue = (newRow.residualRevenue || 0) * retentionMultiplier;
        newRow.commissions = (newRow.commissions || 0) * retentionMultiplier;
        // Fewer reps survive to each milestone, so fewer bonuses are earned.
        // Fees and ad spend stay as modeled.
        newRow.bonuses = row.bonuses * DOUBLE_ATTRITION_SURVIVAL;
        newRow.totalCost = row.totalCost - (row.bonuses - newRow.bonuses);
      }
      newRow.grossProfit = newRow.grossRevenue * (inputs.gpm / 100) - (newRow.commissions || 0);
      newRow.netProfit = newRow.grossProfit - newRow.totalCost;
      return newRow;
    });
  }, [rows, halfSales, doubleAttrition, salesDelay, inputs.gpm, inputs.ticket]);
  const finalRows = useMemo(() => {
    let cumProfit = adjustedRows[0].cumProfit;
    return adjustedRows.map((row, idx) => {
      if (idx === 0) return row;
      cumProfit += row.netProfit;
      return {
        ...row,
        cumProfit
      };
    });
  }, [adjustedRows]);
  // At 24 months the table keeps the Build row and the first three months
  // month by month, then rolls everything after that up into quarters.
  const displayEntries = useMemo<DisplayEntry[]>(() => {
    const windowRows = finalRows.slice(0, timeframe + 1);
    if (timeframe < 24) {
      return windowRows.map((row, idx) => ({
        row,
        months: idx === 0 ? [] : [idx],
        aggregate: false
      }));
    }
    const entries: DisplayEntry[] = windowRows.slice(0, QUARTERLY_FROM_MONTH).map((row, idx) => ({
      row,
      months: idx === 0 ? [] : [idx],
      aggregate: false
    }));
    for (let start = QUARTERLY_FROM_MONTH; start < windowRows.length; start += 3) {
      const group = windowRows.slice(start, start + 3);
      if (group.length === 0) break;
      const groupMonths = group.map((_, i) => start + i);
      entries.push({
        // Months count straight from signing, leader months included.
        row: aggregateRows(group, groupMonths),
        months: groupMonths,
        aggregate: true
      });
    }
    return entries;
  }, [finalRows, timeframe, leaderMonths]);
  const displayRows = useMemo(() => displayEntries.map(entry => entry.row), [displayEntries]);
  const isAggregateRow = (idx: number) => displayEntries[idx]?.aggregate ?? false;
  const hasQuarterlyColumns = timeframe >= 24;
  // Pest: years two and three show only the year-one customer trail.
  const revenueTrail = usesRevenueTrail(inputs.industry);
  useEffect(() => {
    if (!revenueTrail && timeframe === 36) setTimeframe(24);
  }, [revenueTrail, timeframe, setTimeframe]);
  // The underlying model month a displayed column ends on (0 = Build).
  const monthIndexForRow = (idx: number) => {
    const entry = displayEntries[idx];
    if (!entry || entry.months.length === 0) return 0;
    return entry.months[entry.months.length - 1];
  };
  // Heavier divider at quarter boundaries — or at the monthly/quarterly
  // handoff and each year end in the two-year view.
  const isSectionEnd = (idx: number) => {
    if (hasQuarterlyColumns) {
      const month = monthIndexForRow(idx);
      return month === 3 || month === 12 || month === 24 || month === 36;
    }
    return idx > 0 && idx % 3 === 0;
  };
  const getFormula = (metricKey: string, row: RowData, rowIndex: number): string => {
    const monthNum = rowIndex;
    switch (metricKey) {
      case 'activeCampaigns':
        return `Number of active campaigns for this month.\nAdjusting this will recalculate ad spend and hires.`;
      case 'hires':
        return `Formula: Ad Spend ÷ Cost per Hire\n\nActual: ${fmtMoney(row.adSpend)} ÷ CPH = ${row.hires.toFixed(0)} hires`;
      case 'launched':
        const launchRate = row.hires > 0 ? (row.launched / row.hires * 100).toFixed(0) : 0;
        return `Formula: Hires × Launch %\n\nActual: ${row.hires.toFixed(0)} × ${launchRate}% = ${row.launched.toFixed(1)} launched`;
      case 'active30d':
        if (monthNum === 0) return 'Implementation period - no active reps yet';
        if (monthNum === 1) return 'Month 1 - reps not yet at 30 days';
        return `Formula: Previous month's launched reps × Day 30 retention\n\nActual: Single cohort at 30-60 days = ${row.active30d.toFixed(1)} reps`;
      case 'active60d':
        if (monthNum <= 2) return monthNum === 0 ? 'Implementation period' : 'Not enough time elapsed (need 60 days)';
        return `Formula: 2 months ago launched reps × Day 60 retention\n\nActual: Single cohort at 60-90 days = ${row.active60d.toFixed(1)} reps`;
      case 'active90d':
        if (monthNum < 4) return monthNum === 0 ? 'Implementation period' : 'Building cohort (need 90 days)...';
        if (monthNum === 4) {
          return `Formula: First cohort reaching 90 days\n\nActual: ${row.active90d.toFixed(1)} reps (90-120 days old)`;
        }
        return `Formula: Cumulative count of all reps 90+ days old\n\nActual: ${row.active90d.toFixed(1)} (90-120d cohort) + ${row.veterans.toFixed(1)} (120d+ veterans)\n= ${(row.active90d + row.veterans).toFixed(1)} total`;
      case 'veterans':
        if (monthNum <= 4) return 'No veterans yet (need 120+ days)';
        return `Formula: Previous month's 90d+ reps with attrition + new 120d graduates\n\nActual: ${row.veterans.toFixed(1)} veterans (120+ days old)`;
      case 'teamSize':
        const ts_launched = row.launched * (row.launchWeight ?? 0.5);
        if (row.launchWeight !== undefined) {
          return `Summer launch month: every pre-launch hire starts together, so active reps this month = reps who reach day 15 (the placement bonus count) = ${ts_launched.toFixed(1)}\n\n+ Active 30d+: ${row.active30d.toFixed(1)}\n+ Active 60d+: ${row.active60d.toFixed(1)}\n+ Active 90d+: ${(row.active90d + row.veterans).toFixed(1)}\n\nTotal: ${(ts_launched + row.active30d + row.active60d + row.active90d + row.veterans).toFixed(1)}`;
        }
        const ts_30to60 = row.active30d;
        const ts_60to90 = row.active60d;
        const ts_90plus = rowIndex === 4 ? row.active90d : row.active90d + row.veterans;
        return `Formula: Launched (0-30d at 50%) + Active 30d+ + Active 60d+ + Active 90d+\n\nBreakdown:\n• Launched (0-30d): ${ts_launched.toFixed(1)} (50% weight)\n• Active 30d+ (30-60d): ${ts_30to60.toFixed(1)}\n• Active 60d+ (60-90d): ${ts_60to90.toFixed(1)}\n• Active 90d+ (90d+): ${ts_90plus.toFixed(1)}\n\nTotal: ${(ts_launched + ts_30to60 + ts_60to90 + ts_90plus).toFixed(1)}`;
      case 'adSpend':
        const scenarioNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario active but ad spend unchanged' : '';
        return `Total managed ad spend for the month\n\nActual: ${fmtMoney(row.adSpend)}${scenarioNote}`;
      case 'buildFee':
        if (row.buildFee === 0) return 'No implementation fee this month';
        return `Formula: One-time implementation fee\n\nActual: ${fmtMoney(row.buildFee)} (${monthNum === 0 ? 'Implementation, at signing' : `${row.period} payment`})\n\nClick to edit any cell in this row.`;
      case 'platformFee':
        return `Formula: D2D HIRE platform monthly fee\n\nActual: ${fmtMoney(row.platformFee)} (system access & support)`;
      case 'campaignFee':
        return `Formula: ${fmtMoney(pricing.pipelineBucketFee)} per $2,500 bucket of monthly ad spend\n\nActual: ${fmtMoney(row.campaignFee)}`;
      case 'monthlyFees':
        {
          const hasCorporateRecruiter = row.recruiterFee > 0;
          const recruiterBreakdown = hasCorporateRecruiter ? `\nCorporate Recruiter (${Math.round(row.recruiterFee / pricing.recruiterBucketFee)}x): ${fmtMoney(row.recruiterFee)}` : '';
          const totalMonthlyFees = row.platformFee + row.campaignFee + row.recruiterFee;
          const recruiterFormula = hasCorporateRecruiter ? ` + ${fmtMoney(pricing.recruiterBucketFee)} per bucket for the corporate recruiter` : '';
          return `Formula: ${fmtMoney(pricing.partnershipFee)} fixed partnership fee + ${fmtMoney(pricing.pipelineBucketFee)} per $2,500 bucket of monthly ad spend${recruiterFormula}\n\nPartnership (fixed): ${fmtMoney(row.platformFee)}\nPipeline Management (${Math.round(row.campaignFee / pricing.pipelineBucketFee)}x): ${fmtMoney(row.campaignFee)}${recruiterBreakdown}\nTotal: ${fmtMoney(totalMonthlyFees)}`;
        }
      case 'performanceBonuses':
        return `Estimated bonuses\nFormula: ${bonusFormula}\n\nRep counts are estimated from your retention rates and rounded up. Bonuses are always paid in whole increments and billed in arrears.\n\nEstimated: ${fmtMoney(row.bonuses)}\n\nClick the value for the breakdown.`;
      case 'placementBonus':
        {
          return `Formula: ${bonusFormula}\n\nActual: ${fmtMoney(row.bonuses)}`;
        }
      case 'retentionBonus':
        if (monthNum === 0) {
          return 'Implementation period - no bonuses billed yet';
        }
        const prevMonthForRetention = finalRows[monthNum - 1];
        let bonus30d = 0;
        let bonus60d = 0;
        let bonus90d = 0;
        let explanation = "Formula: Previous month's active reps × $250\n";
        explanation += '(Billed month after hitting milestone)\n';
        explanation += 'Rounded down to nearest $250\n\n';
        if (inputs.pkg === 'advantage') {
          const raw30d = prevMonthForRetention.active30d * 250;
          bonus30d = Math.floor(raw30d / 250) * 250;
          explanation += `30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} active × $250 = ${fmtMoney(bonus30d)}\n`;
          const raw60d = prevMonthForRetention.active60d * 250;
          bonus60d = Math.floor(raw60d / 250) * 250;
          explanation += `60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} active × $250 = ${fmtMoney(bonus60d)}\n`;
          const raw90d = prevMonthForRetention.active90d * 250;
          bonus90d = Math.floor(raw90d / 250) * 250;
          explanation += `90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} active × $250 = ${fmtMoney(bonus90d)}\n`;
        }
        const totalBonus = bonus30d + bonus60d + bonus90d;
        explanation += `Total: ${fmtMoney(totalBonus)}`;
        let retentionBreakdown = `\n\nCOHORT BREAKDOWN:\n`;
        retentionBreakdown += `Billing for milestones hit in ${prevMonthForRetention.period}:\n`;
        if (prevMonthForRetention.active30d > 0) {
          const cohortMonth = monthNum - 2;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 30d milestone: ${prevMonthForRetention.active30d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active60d > 0) {
          const cohortMonth = monthNum - 3;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 60d milestone: ${prevMonthForRetention.active60d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        if (prevMonthForRetention.active90d > 0) {
          const cohortMonth = monthNum - 4;
          if (cohortMonth >= 1) {
            retentionBreakdown += `• 90d milestone: ${prevMonthForRetention.active90d.toFixed(1)} reps from ${finalRows[cohortMonth].period} cohort\n`;
          }
        }
        const retentionAttritionNote = doubleAttrition ? '\n\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return explanation + retentionBreakdown + retentionAttritionNote;
      case 'bonuses':
        return `Formula: Launch + Day 30 + Day 60 + Day 90 bonuses\n\nActual: ${fmtMoney(row.bonuses)} total bonuses this month`;
      case 'totalCost':
        return `Formula: Sum of all fees, bonuses, and ad spend (rounded)\n\nActual: ${fmtMoney(row.totalCost)}`;
      case 'totalSales':
        const salesNote = halfSales ? '\n\n⚠️ 50% Sales Scenario: Sales reduced by 50%' : '';
        const attrNote = doubleAttrition ? '\n⚠️ 2x Attrition: Active reps reduced by 50%' : '';
        return `Formula: Ramping rep sales + Veteran sales\n\nActual: Reps in months 1-3 producing ${rampPcts[0]}% / ${rampPcts[1]}% / ${rampPcts[2]}% of a veteran's ${inputs.salesVetMonthly} sales/month + ${row.veterans.toFixed(1)} veterans × ${inputs.salesVetMonthly} sales/month\n= ${row.totalSales.toFixed(1)} total sales${salesNote}${attrNote}`;
      case 'realizationRate':
        return `Formula: Pullthrough / Realization Rate\n\nActual: ${(row.realizationRate * 100).toFixed(0)}% of sales are realized`;
      case 'grossRevenue':
        const revenueNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied to sales' : '';
        const residualNote = row.residualRevenue && row.residualRevenue > 0 ? `\n\nResidual Revenue: ${fmtMoney(row.residualRevenue)}\nInitial Sales Revenue: ${fmtMoney(row.grossRevenue - row.residualRevenue)}` : '';
        return `Formula: Total Sales × Realization Rate × Avg Ticket${residualNote}\n\nActual: ${row.totalSales.toFixed(1)} × ${(row.realizationRate * 100).toFixed(0)}% × ${fmtMoney(inputs.ticket)}\n= ${fmtMoney(row.grossRevenue)}${revenueNote}`;
      case 'grossProfit':
        const profitNote = halfSales || doubleAttrition ? '\n\n⚠️ Scenario adjustments applied' : '';
        if (managerView(inputs)) {
          const backendTiming = overrideBonusMonth(inputs) ? `paid as one bonus in Month ${overrideBonusMonth(inputs)} on accounts whose clawback window has closed by then; accounts still inside their window are paid on a rolling basis as each window closes. No second bonus in years two or three` : 'paid as each month’s clawback window closes';
          return `Manager view: override income\n\nUpfront override (${inputs.overrideUpfrontPct || 0}% of first-year ACV, at sale): ${fmtMoney(row.overrideUpfront ?? 0)}\nBackend override (${inputs.overrideBackendPct || 0}% of ACV, accounts in good standing): ${fmtMoney(row.overrideBackend ?? 0)}\nClawbacks on early cancels: −${fmtMoney(row.overrideClawback ?? 0)}\n= ${fmtMoney(row.grossProfit)}\n\nBackend ${backendTiming}, net of clawbacks.${profitNote}`;
        }
        if (row.commissions !== undefined && row.commissions !== 0) {
          const parts = row.commissionUpfront !== undefined && !halfSales && !doubleAttrition ? `\n  Upfront (on first-year ACV): ${fmtMoney(row.commissionUpfront)}\n  Residual: ${fmtMoney(row.commissionResidual ?? 0)}\n  Backend (window closed): ${fmtMoney(row.commissionBackend ?? 0)}\n  Clawbacks: −${fmtMoney(row.commissionClawback ?? 0)}` : '';
          return `Formula: Gross Revenue × Margin before commissions − Net commissions\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}% − ${fmtMoney(row.commissions)}\n= ${fmtMoney(row.grossProfit)}\n\nNet commissions: ${fmtMoney(row.commissions)}${parts}${profitNote}`;
        }
        return `Formula: Gross Revenue × Margin %\n\nActual: ${fmtMoney(row.grossRevenue)} × ${inputs.gpm}%\n= ${fmtMoney(row.grossProfit)}${profitNote}`;
      case 'netProfit':
        return `Formula: Gross Profit - Total Investment\n\nActual: ${fmtMoney(row.grossProfit)} - ${fmtMoney(row.totalCost)}\n= ${fmtMoney(row.netProfit)}`;
      case 'cumProfit':
        return `Formula: Sum of monthly contribution after recruiting costs, from Implementation to ${row.period}\n\nActual: Running total = ${fmtMoney(row.cumProfit)}`;
      case 'roi':
        let cumGrossProfit = 0;
        let cumTotalCost = 0;
        for (let i = 0; i <= rowIndex; i++) {
          cumGrossProfit += finalRows[i].grossProfit;
          cumTotalCost += finalRows[i].totalCost;
        }
        const roiPercent = cumTotalCost > 0 ? (cumGrossProfit - cumTotalCost) / cumTotalCost * 100 : 0;
        return `Return on recruiting spend\nFormula: Gross Profit To Date ÷ Total Investment To Date\n\nActual: ${fmtMoney(cumGrossProfit)} ÷ ${fmtMoney(cumTotalCost)}\n= ${roiMultiple(roiPercent)}`;
      case 'higherPartnership':
        return `Subtotal of Implementation, Platform, Campaign, and Bonus fees`;
      default:
        return 'Calculation details';
    }
  };
  const metricGroups = [{
    id: 'recruiting',
    label: 'Recruiting',
    color: 'bg-slate-600',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'adSpendPerCampaign',
      label: 'Ad Spend',
      format: (v: number) => fmtMoney(v),
      centered: true
    }, {
      key: 'avgCPH',
      label: 'Average CPH',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'hires',
      label: 'Hired Reps',
      format: (v: number) => v.toLocaleString(),
      bold: true
    }]
  }, {
    id: 'activeReps',
    label: 'Active Reps',
    color: 'bg-slate-700',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'launched',
      label: 'Launched',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active30d',
      label: 'Active 30d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active60d',
      label: 'Active 60d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'active90d',
      label: 'Active 90d+',
      format: (v: number) => v.toFixed(1)
    }, {
      key: 'teamSize',
      label: 'Active Reps (Average)',
      format: (v: number) => v.toFixed(1),
      bold: true,
      calculated: true,
      centered: true
    }]
  }, {
    id: 'investment',
    label: 'Hire Partnership',
    color: 'bg-slate-500',
    collapsible: true,
    showHeaderData: false,
    metrics: [
    // The implementation fee row only exists on schedules that charge one.
    ...(inputs.pkg && pricing.implementationFee > 0 ? [{
      key: 'buildFee' as const,
      label: buildSplitPlans.length > 0 ? <button type="button" onClick={cycleBuildPlan} className="font-inherit underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 rounded text-left" title={activeBuildPlan === 0 ? `Currently charged in full upfront (${fmtMoney(buildFeeTotal)}). Click to split it into payments.` : activeBuildPlan >= buildSplitPlans.length ? `Currently split into ${buildSplitPlans[activeBuildPlan - 1].length} payments. Click to charge it all upfront.` : `Currently split into ${buildSplitPlans[activeBuildPlan - 1].length} payments. Click to spread it further.`} aria-label="Toggle how the implementation fee is charged" data-id="element-1084" data-mp="3bd1k3ja55pm6">
                      Implementation Fee
                    </button> : 'Implementation Fee',
      format: (v: number) => fmtMoney(v),
      editable: true,
      clickToEdit: true
    }] : []), ...(inputs.pkg ? [{
      key: 'monthlyFees' as const,
      label: 'Monthly Fees',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'performanceBonuses' as const,
      label: <button type="button" onClick={() => onInputsChange({
        ...inputs,
        salesSchoolEnabled: inputs.salesSchoolEnabled === false
      })} className="font-inherit underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 rounded" title={inputs.salesSchoolEnabled === false ? 'Restore all bonus charges' : 'Clear all bonus charges'} aria-label={inputs.salesSchoolEnabled === false ? 'Restore all bonus charges' : 'Clear all bonus charges'} data-id="element-1085" data-mp="2g9pskht9z9o6">
                    Estimated Bonuses
                  </button>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'higherPartnership' as const,
      label: 'D2D HIRE Partnership',
      format: (v: number) => fmtMoney(v),
      bold: true
    }] : [])]
  }, {
    id: 'revenue',
    label: 'Revenue & Profit',
    color: 'bg-slate-800',
    collapsible: true,
    showHeaderData: false,
    metrics: [{
      key: 'totalSales',
      label: <div className="flex items-center justify-between w-full group" data-id="element-1086" data-mp="1c658eup7tsop">
              <span data-id="element-1087" data-mp="1c415og31yzq0">Total Sales</span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200" data-id="element-1088" data-mp="ofhxs1nx0oyg">
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.max(0, prev - 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Reduce sales delay (enable sales in earlier months)" data-id="element-1089" data-mp="jfdg48b9to6n">
                  ◀
                </button>
                {salesDelay > 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-1090" data-mp="2rlc6u6rc56ed">
                    {salesDelay}mo
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setSalesDelay(prev => Math.min(11, prev + 1));
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Delay sales (zero out sales in early months)" data-id="element-1091" data-mp="2mkoi5rqkx1nb">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => v.toFixed(1),
      bold: true
    }, {
      key: 'realizationRate',
      label: 'Realization Rate',
      format: (v: number) => `${(v * 100).toFixed(0)}%`
    }, ...(inputs.revenueCadence !== 'one-time' ? [{
      key: 'residualRevenue' as const,
      label: inputs.revenueCadence === 'monthly' ? 'Monthly Residual Revenue' : 'Quarterly Residual Revenue',
      format: (v: number) => fmtMoney(v)
    }] : []), {
      key: 'grossRevenue',
      label: <div className="flex items-center justify-between w-full group" data-id="element-1092" data-mp="s6amq0gipwky">
              <span data-id="element-1093" data-mp="s8eyt5okm1wb">
                {inputs.revenueCadence !== 'one-time' ? 'Total Monthly Revenue' : 'Monthly Gross Revenue'}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1 rounded shadow-sm border border-gray-200 relative z-20 mr-1" data-id="element-1094" data-mp="123yqffw3qef9">
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev - 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Shorten the delay between sale and cash collection" data-id="element-1095" data-mp="33wmyc2joxq1d">
                  ◀
                </button>
                {revenueShift !== 0 && <span className="text-[9px] font-mono font-bold text-amber-600" data-id="element-1096" data-mp="3es6ruf6d3moy">
                    +{revenueShift}mo
                  </span>}
                <button onClick={e => {
            e.stopPropagation();
            setRevenueShift(prev => prev + 1);
          }} className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-amber hover:bg-amber-50 rounded transition-colors text-[10px]" title="Collect revenue a month after the sale (sales stay put; shared with the proposal)" data-id="element-1097" data-mp="3oj1evlrfmqbg">
                  ▶
                </button>
              </div>
            </div>,
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'grossProfit',
      label: managerView(inputs) ? 'Override Income' : 'Monthly Gross Profit',
      format: (v: number) => fmtMoney(v)
    }, {
      key: 'totalCost',
      label: 'Total Investment',
      format: (v: number) => fmtMoney(v),
      bold: true
    }, {
      key: 'netProfit',
      label: 'Contribution After Recruiting Costs',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      bold: true
    }, {
      key: 'cumProfit',
      label: 'Profit To Date',
      format: (v: number) => fmtMoney(v),
      highlight: true,
      featured: true
    }, {
      key: 'roi',
      label: 'Return on Recruiting Spend',
      format: (v: number) => roiMultiple(v),
      highlight: true,
      bg: 'bg-gray-200'
    }]
  }];
  return <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg" data-id="element-1098" data-mp="hq01fl28xm98">
      <div className="flex items-center justify-between mb-4" data-id="element-1099" data-mp="cuugopkkydmq">
        <h2 className="text-lg md:text-xl font-bold text-navy" data-id="element-1100" data-mp="7wsuvwvrs07k">
          {inputs.companyName ? `Monthly Projections for ${inputs.companyName}` : 'Monthly Projections'}
        </h2>

        <div className="flex items-center gap-3" data-id="element-1101" data-mp="3v1r08breie5p">
          {/* Timeframe Toggle */}
          <div className="flex gap-1 bg-white border-2 border-gray-200 rounded p-1" data-id="element-1102" data-mp="3q4ie6034tpz6">
            <button onClick={() => setTimeframe(3)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 3 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1103" data-mp="3l55s3hk34mxn">
              3M
            </button>
            <button onClick={() => setTimeframe(6)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 6 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1104" data-mp="1qws50tpxjyiu">
              6M
            </button>
            <button onClick={() => setTimeframe(12)} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 12 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1105" data-mp="25lex7w0nokrh">
              12M
            </button>
            <button onClick={() => setTimeframe(24)} title="Two years — months 4 and later are grouped by quarter" className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 24 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1106" data-mp="1cytxa76470cb">
              24M
            </button>
            {revenueTrail && <button onClick={() => setTimeframe(36)} title="Three years — the year-one customer trail, attriting at your selected rate" className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${timeframe === 36 ? 'bg-amber text-navy' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1107" data-mp="2yc6nkcv1if6w">
                3Y
              </button>}
          </div>

          {/* Scenario Toggles */}
          <button onClick={() => onInputsChange({
          ...inputs,
          pkg: inputs.pkg ? '' : 'advantage'
        })} title="Include D2D HIRE partnership fees in the projection" aria-pressed={!!inputs.pkg} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${inputs.pkg ? 'bg-amber text-navy border-amber' : 'bg-white text-gray-700 border-gray-300 hover:border-amber'}`} data-id="element-1108" data-mp="fiebqlpth4ni">
            {inputs.pkg ? '✓ ' : ''}D2D HIRE
          </button>
          <button onClick={() => onInputsChange({
          ...inputs,
          addRecruiter: !inputs.addRecruiter
        })} title={`Add a dedicated corporate recruiter: ${fmtMoney(pricing.recruiterBucketFee)} per $2,500 of ad spend${pricing.recruiterBuildUpcharge > 0 ? `, plus ${fmtMoney(pricing.recruiterBuildUpcharge)} on the implementation fee` : ' per month'}`} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${inputs.addRecruiter ? 'bg-slate-600 text-white border-slate-700' : 'bg-white text-gray-700 border-gray-300 hover:border-slate-500'}`} data-id="element-1109" data-mp="3dsocj9rdbm8p">
            {inputs.addRecruiter ? '✓ ' : ''}Add Recruiter
          </button>
          <button onClick={() => onInputsChange({
          ...inputs,
          leaderCampaignMonths: (leaderMonths + 1) % (LEADER_CAMPAIGN_MAX_MONTHS + 1),
          leaderMonth2Sales: undefined
        })} title={`Click once for a one-month leader search, again to model it taking two months. Each search month defaults to ${fmtMoney(LEADER_CAMPAIGN_MONTHLY_AD_SPEND)} ad spend with a ${fmtMoney(pricing.leaderPartnershipFee)} partnership fee plus ${fmtMoney(pricing.pipelineBucketFee)} pipeline management per $2,500 of ad spend — adjust the spend in the table.${pricing.leaderBuildUpcharge > 0 ? ` Plus ${fmtMoney(pricing.leaderBuildUpcharge)} one-time.` : ''} The leader earns the standard ${bonusSummary} like every rep.`} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${leaderMonths > 0 ? 'bg-navy text-white border-navy' : 'bg-white text-gray-700 border-gray-300 hover:border-navy'}`} data-id="element-1110" data-mp="3rmpfygt9lxfe">
            {leaderMonths > 0 ? `✓ Leader Campaign · ${leaderMonths} mo` : 'Leader Campaign'}
          </button>
          <button onClick={toggleSummerProgram} aria-pressed={!!inputs.summerProgram} title="Summer program preset: ads paused Months 6–12, sales paused Months 1–3 and 10–12. You can still edit any month afterwards; turning it off undoes only these pauses." className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${inputs.summerProgram ? 'bg-amber text-navy border-amber' : 'bg-white text-gray-700 border-gray-300 hover:border-amber'}`} data-id="element-1111" data-mp="2u3miwd7r0kpp">
            {inputs.summerProgram ? '✓ ' : ''}Summer Program
          </button>
          <button onClick={onToggleLowRetention} aria-pressed={lowRetention} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${lowRetention ? 'bg-gray-700 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} data-id="element-1112" data-mp="14ixe9blhycsm">
            {lowRetention ? '✓ ' : ''}Low
          </button>
          <button onClick={onToggleOnTarget} aria-pressed={onTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-1113" data-mp="1nbhv1unx8rg2">
            {onTarget ? '✓ ' : ''}On Target
          </button>
          <button onClick={onPlusFive} aria-pressed={plusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-1114" data-mp="1qdk7grszpmiq">
            {plusFive ? '✓ ' : ''}+5%
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded border-2 border-gray-200" data-id="element-1115" data-mp="29539avba2uj6">
        <table className="w-full border-collapse text-sm" style={{
        tableLayout: 'fixed'
      }} data-id="element-1116" data-mp="2e3woqta4kvlz">
          <thead className="sticky top-0 z-20" data-id="element-1117" data-mp="7dtcjtifd5lt">
            <tr className="bg-navy text-white" data-id="element-1118" data-mp="7fny9a1it1qy">
              <th className="bg-navy text-xs font-extrabold uppercase tracking-wide p-2 text-left border-r border-gray-300" style={{
              width: '170px'
            }} data-id="element-1119" data-mp="2f9o9mgczhqa">
                <span className="inline-block" data-id="element-1120" data-mp="3on9i7fmgkm33">Metric</span>
              </th>
              {displayRows.map((row, idx) => {
              // Leader search columns stack onto two lines to stay narrow.
              const searchMatch = row.period.match(/^Month (\d+) \(leader\)$/);
              const displayPeriod = row.period === 'Implementation' ? <span className="inline-block whitespace-normal leading-tight" data-id="element-1121" data-mp="bgg8o6m4pcot">
                      Imple-
                      <br data-id="element-1122" data-mp="18w3s1arqgq6g" />
                      mentation
                    </span> : searchMatch ? <span className="inline-block whitespace-normal leading-tight" data-id="element-1123" data-mp="1430xp0f4tcp7">
                      Mo {searchMatch[1]}
                      <br data-id="element-1124" data-mp="33baodme2nqj1" />
                      (Leader)
                    </span> : row.period.replace('Month', 'Mo');
              const isQuarterEnd = isSectionEnd(idx);
              const isColHighlighted = hoveredCell !== null && hoveredCell.colIdx === idx;
              return <th key={row.period} className={`text-xs font-extrabold uppercase tracking-wide p-2 text-center whitespace-nowrap border-r border-gray-300 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isColHighlighted ? 'bg-navy/80 text-amber' : 'bg-navy'}`} data-id="element-1125" data-mp="2fkenlzrn1kzn" data-mpi={idx}>
                    {displayPeriod}
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="bg-white" data-id="element-1126" data-mp="8vrwe3xkvhjz">
            {metricGroups.filter(group => group.metrics.length > 0).map((group, groupIdx) => {
            const isExpanded = group.collapsible ? expandedSections[group.id as keyof typeof expandedSections] : true;
            const showDetails = !group.collapsible || isExpanded;
            return <Fragment key={group.id} data-id="element-1127" data-mp="1ybcf4506ezn8" data-mpi={groupIdx}>
                  {!group.noHeader && <tr className={`${group.color}`} data-id="element-1128" data-mp="2c391dsf5yfbp">
                      <td className={`${group.color} p-2 font-extrabold text-white text-xs border-r border-gray-300 ${group.collapsible ? 'cursor-pointer hover:bg-opacity-80' : ''}`} onClick={() => group.collapsible && toggleSection(group.id as keyof typeof expandedSections)} data-id="element-1129" data-mp="2c5v9pu9voyuz">
                        <div className="flex items-center gap-2" data-id="element-1130" data-mp="mqf60ueq45ch">
                          {group.collapsible && <span className="text-white" data-id="element-1131" data-mp="39ze8dytqaeuv">
                              {isExpanded ? '▼' : '▶'}
                            </span>}
                          <span className="uppercase tracking-wide text-xs inline-block" data-id="element-1132" data-mp="3jvz9i92mn5tf">
                            {group.label}
                          </span>
                        </div>
                      </td>
                      {displayRows.map((row, rowIdx) => {
                  const isQuarterEnd = isSectionEnd(rowIdx);
                  return <td key={row.period} className={`${group.color} p-2 text-center font-extrabold text-white border-r border-gray-300 ${isQuarterEnd ? 'border-r-4' : ''}`} data-id="element-1133" data-mp="306fec1fgpw3v" data-mpi={rowIdx}></td>;
                })}
                    </tr>}

                  {showDetails && group.metrics.map((metric, metricIdx) => {
                const isLastInGroup = metricIdx === group.metrics.length - 1;
                const isRowHighlighted = hoveredMetricKey === metric.key;
                const isCrosshairRow = hoveredCell !== null && hoveredCell.metricKey === metric.key;
                return <tr key={metric.key} className={`h-10 ${metric.featured ? 'bg-amber-light' : 'bg-white'}`} data-id="element-1134" data-mp="1m1lqw5mj3ghq" data-mpi={metricIdx}>
                          <td className={`p-2 ${group.noHeader ? '' : 'pl-4'} text-gray-900 ${metric.bold || metric.featured ? 'font-extrabold text-xs' : 'font-bold text-xs'} border-r border-gray-300 transition-colors cursor-pointer whitespace-nowrap relative ${isRowHighlighted || isCrosshairRow ? 'bg-amber-50' : metric.featured ? 'bg-amber-light' : 'bg-white'}`} onMouseEnter={() => {
                    setHoveredMetricKey(metric.key);
                    setHoveredCell(null);
                  }} onMouseLeave={() => setHoveredMetricKey(null)} data-id="element-1135" data-mp="1c9z6pjs7q72u">
                            <span className="inline-block" data-id="element-1136" data-mp="jnrb5nifc90i">{metric.label}</span>
                          </td>
                          {displayRows.map((row, rowIdx) => {
                    let value: number;
                    let displayValue: React.ReactNode;
                    // Quarterly columns roll up several model months.
                    const isAggregate = isAggregateRow(rowIdx);
                    const monthIdx = monthIndexForRow(rowIdx);
                    // Milestone bonuses are computed by the model.
                    const getPlacementBonus = (r: RowData, _idx: number) => r.bonuses;
                    const getRetentionBonus = () => 0;
                    const getTotalAdSpend = (idx: number) => {
                      if (idx === 0) return 0;
                      return row.adSpend;
                    };
                    const getAvgCPH = (idx: number) => {
                      if (idx === 0) return 0;
                      // Quarterly columns blend their own months.
                      if (isAggregate) return row.hires > 0 ? row.adSpend / row.hires : inputs.cph;
                      // Leader search months use their own blended CPH.
                      if (isLeaderMonth(idx)) return LEADER_CAMPAIGN_CPH;
                      return inputs.cph;
                    };
                    const getBuildFeeValue = (idx: number) => {
                      // No package selected → ignore any stored build fees
                      if (!inputs.pkg) return 0;
                      // Quarterly columns already sum their build fees.
                      if (isAggregate) return row.buildFee;
                      // No custom payment plans without an
                      // implementation fee.
                      if (pricing.implementationFee === 0) return row.buildFee;
                      const customBuildFees = inputs.monthlyBuildFees;
                      if (customBuildFees && customBuildFees[idx] !== undefined && customBuildFees[idx] !== null) {
                        return customBuildFees[idx];
                      }
                      return row.buildFee;
                    };
                    if (metric.key === 'teamSize') {
                      const launched0to30 = row.launched * (row.launchWeight ?? 0.5);
                      const active30to60 = row.active30d;
                      const active60to90 = row.active60d;
                      const active90plus = row.active90d + row.veterans;
                      value = launched0to30 + active30to60 + active60to90 + active90plus;
                    } else if (metric.key === 'active30d') {
                      value = row.active30d;
                    } else if (metric.key === 'active60d') {
                      value = row.active60d;
                    } else if (metric.key === 'active90d') {
                      if (monthIdx === 4) {
                        value = row.active90d;
                      } else if (monthIdx > 4) {
                        value = row.active90d + row.veterans;
                      } else {
                        value = 0;
                      }
                    } else if (metric.key === 'adSpendPerCampaign') {
                      value = getTotalAdSpend(rowIdx);
                    } else if (metric.key === 'avgCPH') {
                      value = getAvgCPH(rowIdx);
                    } else if (metric.key === 'placementBonus') {
                      value = getPlacementBonus(row, rowIdx);
                    } else if (metric.key === 'retentionBonus') {
                      value = getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'performanceBonuses') {
                      value = getPlacementBonus(row, rowIdx) + getRetentionBonus(row, rowIdx);
                    } else if (metric.key === 'buildFee') {
                      value = getBuildFeeValue(rowIdx);
                    } else if (metric.key === 'monthlyFees') {
                      value = row.platformFee + row.campaignFee + row.recruiterFee;
                    } else if (metric.key === 'higherPartnership') {
                      const pBonus = getPlacementBonus(row, rowIdx);
                      const rBonus = getRetentionBonus(row, rowIdx);
                      const buildFeeVal = getBuildFeeValue(rowIdx);
                      value = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + row.leaderRevShare + pBonus + rBonus;
                    } else if (metric.key === 'totalCost') {
                      if (!inputs.pkg) {
                        // No package selected: only ad spend contributes
                        value = row.adSpend;
                      } else {
                        const pBonus = getPlacementBonus(row, rowIdx);
                        const rBonus = getRetentionBonus(row, rowIdx);
                        const buildFeeVal = getBuildFeeValue(rowIdx);
                        const subtotal = buildFeeVal + row.platformFee + row.campaignFee + row.recruiterFee + row.leaderRevShare + pBonus + rBonus;
                        value = subtotal + row.adSpend;
                      }
                    } else if (metric.key === 'roi') {
                      let cumGrossProfit = 0;
                      let cumTotalCost = 0;
                      for (let i = 0; i <= monthIdx; i++) {
                        cumGrossProfit += finalRows[i].grossProfit;
                        cumTotalCost += finalRows[i].totalCost;
                      }
                      // ROI = (gain − cost) ÷ cost
                      value = cumTotalCost > 0 ? (cumGrossProfit - cumTotalCost) / cumTotalCost * 100 : 0;
                    } else if (metric.key === 'netProfit' && !inputs.pkg) {
                      // No package: net profit = gross profit − ad spend only
                      value = row.grossProfit - row.adSpend;
                    } else if (metric.key === 'cumProfit' && !inputs.pkg) {
                      // No package: profit-to-date sums (grossProfit − adSpend) across rows
                      let cum = 0;
                      for (let i = 0; i <= monthIdx; i++) {
                        cum += finalRows[i].grossProfit - finalRows[i].adSpend;
                      }
                      value = cum;
                    } else {
                      value = row[metric.key as keyof RowData] as number;
                    }
                    const formatted = metric.format(value);
                    if (isAggregate) {
                      // Quarterly roll-ups are read-only summaries.
                      displayValue = formatted;
                    } else if (metric.key === 'activeCampaigns' && metric.editable && rowIdx > 0 && !isLeaderMonth(rowIdx)) {
                      if (editingCampaign === rowIdx) {
                        displayValue = <input type="number" min="0" step="0.5" value={editCampaignValue} onChange={e => setEditCampaignValue(e.target.value)} onKeyDown={handleCampaignKeyDown} onBlur={saveCampaignEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1137" data-mp="22io6xs5sph4m" />;
                      } else {
                        displayValue = <div className="relative flex items-center justify-center group/cell" data-id="element-1138" data-mp="2c60hn3pm8d5s">
                                    {rowIdx > 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushLeft(rowIdx);
                          }} title={`← Copy ${formatted} to all earlier months`} className="absolute left-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1139" data-mp="236dk4t00bri0">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-1140" data-mp="3r6qyewsnefm1">
                                          <path d="M11 7H3M6 4L3 7l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1141" data-mp="8t193wsuqla1" />
                                        </svg>
                                      </button>}
                                    <span onClick={() => startEditingCampaign(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-1142" data-mp="2ask6zlvcw789">
                                      {formatted}
                                    </span>
                                    {rowIdx < displayRows.length - 1 && <button onClick={e => {
                            e.stopPropagation();
                            handlePushRight(rowIdx);
                          }} title={`Copy ${formatted} to all months from here →`} className="absolute right-1 opacity-0 group-hover/cell:opacity-100 transition-opacity text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1143" data-mp="37htap3zr88dj">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-1144" data-mp="33eijkeiioyee">
                                          <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1145" data-mp="2ygqtlq1fnmcu" />
                                        </svg>
                                      </button>}
                                  </div>;
                      }
                    } else if (metric.key === 'activeCampaigns' && isLeaderMonth(rowIdx)) {
                      // Leader pilot months aren't a campaign multiple.
                      displayValue = 'Leader';
                    } else if (metric.key === 'activeCampaigns' && rowIdx === 0) {
                      displayValue = formatted;
                    } else if (metric.key === 'buildFee' && metric.clickToEdit) {
                      if (editingBuildFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editBuildFeeValue} onChange={e => setEditBuildFeeValue(e.target.value)} onKeyDown={handleBuildFeeKeyDown} onBlur={saveBuildFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1146" data-mp="363tcjcr8mpo3" />;
                      } else {
                        displayValue = <span onClick={() => startEditingBuildFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-1147" data-mp="9q16zgbu4xcp">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'platformFee' && metric.clickToEdit) {
                      if (editingPlatformFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editPlatformFeeValue} onChange={e => setEditPlatformFeeValue(e.target.value)} onKeyDown={handlePlatformFeeKeyDown} onBlur={savePlatformFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1148" data-mp="346cwd95eejh8" />;
                      } else {
                        displayValue = <span onClick={() => startEditingPlatformFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-1149" data-mp="jurfy65np1qf">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'campaignFee' && metric.clickToEdit) {
                      if (editingCampaignFee === rowIdx) {
                        displayValue = <input type="number" min="0" step="100" value={editCampaignFeeValue} onChange={e => setEditCampaignFeeValue(e.target.value)} onKeyDown={handleCampaignFeeKeyDown} onBlur={saveCampaignFeeEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1150" data-mp="2cgmfw2pa8tc1" />;
                      } else {
                        displayValue = <span onClick={() => startEditingCampaignFee(rowIdx, value)} className="cursor-pointer hover:text-amber transition-colors" data-id="element-1151" data-mp="apvnfet5pua8">
                                    {formatted}
                                  </span>;
                      }
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx > 0 && editingAdSpend === rowIdx) {
                      displayValue = <input type="number" min="0" step="100" value={editAdSpendValue} onChange={e => setEditAdSpendValue(e.target.value)} onKeyDown={handleAdSpendKeyDown} onBlur={saveAdSpendEdit} autoFocus className="w-24 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1152" data-mp="25u220o1kizo9" />;
                    } else if (metric.key === 'adSpendPerCampaign' && isLeaderMonth(rowIdx)) {
                      // Leader search spend steps in $2,500 buckets;
                      // the pipeline fee scales with it.
                      displayValue = <div className="relative grid h-12 grid-rows-[12px_1fr_12px] items-center justify-center group/cell" data-id="element-1153" data-mp="3udg45c0er1ve">
                                  <button type="button" onClick={e => {
                          e.stopPropagation();
                          setLeaderAdSpend(rowIdx, (Math.floor(value / CAMPAIGN_SPEND_STEP) + 1) * CAMPAIGN_SPEND_STEP);
                        }} title={`Increase leader ad spend by $2,500 (adds a ${fmtMoney(pricing.pipelineBucketFee)} pipeline bucket)`} aria-label="Increase leader ad spend by $2,500" className="row-start-1 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1154" data-mp="3qf07shifsh5b">
                                    ↑
                                  </button>
                                  {value > CAMPAIGN_SPEND_STEP && <button type="button" onClick={e => {
                          e.stopPropagation();
                          setLeaderAdSpend(rowIdx, Math.max(CAMPAIGN_SPEND_STEP, (Math.ceil(value / CAMPAIGN_SPEND_STEP) - 1) * CAMPAIGN_SPEND_STEP));
                        }} title="Decrease leader ad spend by $2,500" aria-label="Decrease leader ad spend by $2,500" className="row-start-3 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1155" data-mp="1u6z43q1i996u">
                                      ↓
                                    </button>}
                                  <span onClick={() => startEditingAdSpend(rowIdx, value)} title="Click to type a custom leader ad spend" className="row-start-2 self-center cursor-pointer hover:text-amber transition-colors" data-id="element-1156" data-mp="6l6399194b02">
                                    {formatted}
                                  </span>
                                </div>;
                    } else if (metric.key === 'adSpendPerCampaign' && rowIdx > 0 && !isLeaderMonth(rowIdx)) {
                      displayValue = <div className="relative grid h-12 grid-rows-[12px_1fr_12px] items-center justify-center group/cell" data-id="element-1157" data-mp="2r5ebodh2efd0">
                                  <button type="button" onClick={e => {
                          e.stopPropagation();
                          stepAdSpend(rowIdx, 1);
                        }} title="Increase to the next $2,500 tier" aria-label="Increase to the next $2,500 tier" className="row-start-1 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1158" data-mp="2w24rczoyj8xw">
                                    ↑
                                  </button>
                                  {value > 0 && <button type="button" onClick={e => {
                          e.stopPropagation();
                          stepAdSpend(rowIdx, -1);
                        }} title={value <= CAMPAIGN_SPEND_STEP ? 'Pause ads this month ($0, no new hires or fees)' : 'Decrease to the previous $2,500 tier'} aria-label={value <= CAMPAIGN_SPEND_STEP ? 'Pause ads this month' : 'Decrease to the previous $2,500 tier'} className="row-start-3 justify-self-center leading-3 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1159" data-mp="1xhw5b1an0brs">
                                      ↓
                                    </button>}
                                  {rowIdx > 1 && <button type="button" onClick={e => {
                          e.stopPropagation();
                          handlePushAdSpendLeft(rowIdx);
                        }} title={`← Copy ${formatted} to all earlier months`} aria-label="Copy this ad spend to all earlier months" className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1160" data-mp="3lfoocyxhji71">
                                      ←
                                    </button>}
                                  <span onClick={() => startEditingAdSpend(rowIdx, value)} title={value === 0 ? 'Ads paused: no new hires this month. Use ↑ or click to resume.' : 'Click to type a custom monthly ad spend ($0 pauses the month)'} className={`row-start-2 self-center cursor-pointer hover:text-amber transition-colors ${value === 0 ? 'text-gray-400 text-xs uppercase tracking-wide' : ''}`} data-id="element-1161" data-mp="27hy2tu5vpfye">
                                    {value === 0 ? 'Paused' : formatted}
                                  </span>
                                  {rowIdx < displayRows.length - 1 && <button type="button" onClick={e => {
                          e.stopPropagation();
                          handlePushAdSpendRight(rowIdx);
                        }} title={`Copy ${formatted} to all later months →`} aria-label="Copy this ad spend to all later months" className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 text-gray-400 hover:text-amber active:scale-95 transition-all" data-id="element-1162" data-mp="39jzxud2ctk5">
                                      →
                                    </button>}
                                </div>;
                    } else if (metric.key === 'adSpendPerCampaign' && (rowIdx === 0 || isLeaderMonth(rowIdx))) {
                      displayValue = formatted;
                    } else if (metric.key === 'monthlyFees' && value > 0) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setFeePopup({
                          rowIdx,
                          platformFee: row.platformFee,
                          campaignFee: row.campaignFee,
                          recruiterFee: row.recruiterFee,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-1163" data-mp="nng33zu10f7q">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'performanceBonuses' && value > 0) {
                      const milestones = getBonusMilestones(rowIdx);
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        setBonusPopup({
                          rowIdx,
                          ...milestones,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-1164" data-mp="u0r67pnplisf">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'totalSales' && rowIdx > 0 && isSalesPaused(rowIdx)) {
                      // Sales paused: reps stay on the team and keep
                      // attriting, but close nothing this month.
                      displayValue = <button type="button" onClick={e => {
                        e.stopPropagation();
                        toggleSalesPause(rowIdx);
                      }} title="Sales paused this month. Click to resume." aria-label={`Resume sales in ${row.period}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-gray-400 hover:text-amber transition-colors" data-id="element-1165" data-mp="1ub0j7sqmwj8m">
                                  <PlayIcon className="w-3 h-3" aria-hidden="true" data-id="element-1166" data-mp="1l5xiig3wijz7" />
                                  Paused
                                </button>;
                    } else if (metric.key === 'totalSales' && isLeaderSalesMonth(rowIdx) && editingLeaderSales) {
                      displayValue = <input type="number" min="0" step="0.5" value={editLeaderSalesValue} onChange={e => setEditLeaderSalesValue(e.target.value)} onKeyDown={handleLeaderSalesKeyDown} onBlur={saveLeaderSalesEdit} autoFocus className="w-20 text-center border-2 border-amber rounded p-1 text-xs font-semibold focus:border-amber outline-none bg-white" data-id="element-1167" data-mp="xgmqpo4j3zx7" />;
                    } else if (metric.key === 'totalSales' && isLeaderSalesMonth(rowIdx)) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        setEditLeaderSalesValue(value.toFixed(1));
                        setEditingLeaderSales(true);
                      }} title={`Click to set Month ${leaderMonths} (leader) sales for the leader campaign`} className="cursor-pointer underline decoration-dotted decoration-amber underline-offset-2 hover:text-amber transition-colors" data-id="element-1168" data-mp="2u9tq45zashn0">
                                  {formatted}
                                </span>;
                    } else if (metric.key === 'totalSales' && isLeaderMonth(rowIdx)) {
                      // No sales are projected during the leader pilot's
                      // first month, so there's nothing to explain.
                      displayValue = formatted;
                    } else if (metric.key === 'totalSales' && rowIdx > 0) {
                      displayValue = <span onClick={e => {
                        e.stopPropagation();
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        // Scale the two lines so they always add up
                        // to the number shown.
                        const vetRate = inputs.salesVetMonthly;
                        const vetSales = row.veteranReps * vetRate;
                        const baseTotal = row.rampingSales + vetSales;
                        const factor = baseTotal > 0 ? value / baseTotal : 0;
                        setSalesPopup({
                          rowIdx,
                          period: row.period,
                          totalSales: value,
                          rampingReps: row.rampingReps * factor,
                          rampingSales: row.rampingSales * factor,
                          veterans: row.veteranReps * factor,
                          veteranSales: vetSales * factor,
                          x: rect.left + rect.width / 2,
                          y: rect.bottom + 6
                        });
                      }} title="Click to see how this number is calculated" className="cursor-pointer underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-amber transition-colors" data-id="element-1169" data-mp="yh6kl7zqz4zx">
                                  {formatted}
                                </span>;
                      if (!isLeaderMonth(rowIdx)) {
                        displayValue = <span className="relative inline-flex items-center group/sales" data-id="element-1170" data-mp="k242mp7f0vjw">
                                    {displayValue}
                                    <button type="button" onClick={e => {
                            e.stopPropagation();
                            toggleSalesPause(rowIdx);
                          }} title="Pause sales this month (reps stay and keep attriting)" aria-label={`Pause sales in ${row.period}`} className="absolute -right-4 opacity-0 group-hover/sales:opacity-100 focus:opacity-100 text-gray-400 hover:text-amber transition-opacity" data-id="element-1171" data-mp="baln8giamjrv">
                                      <PauseIcon className="w-3 h-3" aria-hidden="true" data-id="element-1172" data-mp="1u8qb9vpg4jxh" />
                                    </button>
                                  </span>;
                      }
                    } else {
                      displayValue = formatted;
                    }
                    const isQuarterEnd = isSectionEnd(rowIdx);
                    let cellClass = `p-2 text-center whitespace-nowrap relative text-sm font-semibold border-r border-gray-300 hover:bg-gray-100 transition-colors ${isQuarterEnd ? 'border-r-4' : ''} ${isRowHighlighted ? 'bg-amber-50' : metric.featured ? 'bg-amber-light' : 'bg-white'}`;
                    if (metric.highlight) {
                      if (value >= 0) {
                        cellClass += ' text-green-600 font-extrabold';
                      } else {
                        cellClass += ' text-red-600 font-extrabold';
                      }
                    } else if (metric.bold) {
                      cellClass += ' font-extrabold text-gray-900';
                    } else {
                      cellClass += ' text-gray-800';
                    }
                    return <td key={row.period} className={cellClass} onMouseEnter={() => {
                      setHoveredCell({
                        metricKey: metric.key,
                        colIdx: rowIdx
                      });
                      setHoveredMetricKey(null);
                    }} onMouseLeave={() => setHoveredCell(null)} data-id="element-1173" data-mp="3mx7rnpannhjs" data-mpi={rowIdx}>
                                {displayValue}
                              </td>;
                  })}
                        </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {hasQuarterlyColumns && <p className="mt-3 text-xs text-gray-600" data-id="element-1174" data-mp="3phxeph0no2fy">
          {revenueTrail ? `After Month 12 the model shows only the revenue trail from what was sold in year one: no new ads, hires, fees or sales, just the year-one customers who stay, attriting at ${inputs.revenueCadence === 'quarterly' ? `${inputs.quarterlyAttrition || 5}% a quarter` : `${inputs.monthlyAttrition || 2}% a month`}. Bonuses and commissions already earned on year-one reps and sales still land as they fall due.` : 'Year two continues at the Month 12 campaign pace.'}{' '}
          Months 4–{timeframe} are grouped by quarter: costs, sales, and
          revenue are quarterly totals, rep counts are quarterly averages, and
          those columns can&rsquo;t be edited directly.
        </p>}

      {tooltip && <div className="fixed z-50 bg-navy text-white p-3 rounded shadow-xl border-2 border-amber max-w-sm text-xs whitespace-pre-line pointer-events-none" style={{
      left: tooltip.x,
      top: tooltip.y
    }} data-id="element-1175" data-mp="2inznpt5fc732">
          {tooltip.content}
        </div>}

      {feePopup && <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setFeePopup(null)} data-id="element-1176" data-mp="31mtzqudi6tfj" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: feePopup.x,
        top: feePopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-1177" data-mp="36l5d3f30bm2i">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-1178" data-mp="14vjfxpudp5hn">
              <span className="font-bold text-navy" data-id="element-1179" data-mp="19unoce9haz6x">
                Monthly Fees Breakdown
              </span>
              <button onClick={() => setFeePopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-1180" data-mp="1enp56rt8x8t5">
                ×
              </button>
            </div>
            <div className="space-y-1.5" data-id="element-1181" data-mp="qwju0py934p6">
              <div className="flex items-center justify-between gap-4" data-id="element-1182" data-mp="qxd0rqx7lu7b">
                <span className="text-gray-600" data-id="element-1183" data-mp="uy598hy9bv2j">Partnership (fixed)</span>
                <span className="font-semibold text-gray-900" data-id="element-1184" data-mp="zx9jzd0ty0uz">
                  {fmtMoney(feePopup.platformFee)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4" data-id="element-1185" data-mp="26d25absuzkje">
                <span className="text-gray-600" data-id="element-1186" data-mp="2bbn4c7wdjy69">
                  Pipeline Management (
                  {Math.round(feePopup.campaignFee / pricing.pipelineBucketFee)}x)
                </span>
                <span className="font-semibold text-gray-900" data-id="element-1187" data-mp="2gh8sf9twsgdt">
                  {fmtMoney(feePopup.campaignFee)}
                </span>
              </div>
              {feePopup.recruiterFee > 0 && <div className="flex items-center justify-between gap-4" data-id="element-1188" data-mp="27o5gx72tqdlt">
                  <span className="text-gray-600" data-id="element-1189" data-mp="22pu36l9eriyb">
                    Corporate Recruiter (
                    {Math.round(feePopup.recruiterFee / pricing.recruiterBucketFee)}x)
                  </span>
                  <span className="font-semibold text-gray-900" data-id="element-1190" data-mp="1ss7o9ys3zrec">
                    {fmtMoney(feePopup.recruiterFee)}
                  </span>
                </div>}
              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-gray-200" data-id="element-1191" data-mp="3n4nreqplypez">
                <span className="font-bold text-navy" data-id="element-1192" data-mp="3j4vddyhg3r28">Total</span>
                <span className="font-bold text-navy" data-id="element-1193" data-mp="1ghgcre5ufm2q">
                  {fmtMoney(feePopup.platformFee + feePopup.campaignFee + feePopup.recruiterFee)}
                </span>
              </div>
            </div>
          </div>
        </>}

      {bonusPopup && <>
          <div className="fixed inset-0 z-40" onClick={() => setBonusPopup(null)} data-id="element-1194" data-mp="1z86gt675hex6" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: bonusPopup.x,
        top: bonusPopup.y,
        transform: 'translateX(-50%)',
        minWidth: 200
      }} data-id="element-1195" data-mp="24710sbgut9iv">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-1196" data-mp="es34939wgen2">
              <span className="font-bold text-navy" data-id="element-1197" data-mp="jrph3bltfsvc">Estimated Bonuses Breakdown</span>
              <button onClick={() => setBonusPopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-1198" data-mp="b9vwtymp363o">
                ×
              </button>
            </div>
            <div className="space-y-1.5" data-id="element-1199" data-mp="l6ieyfnmzj20">
              <div className="flex items-center justify-between gap-4" data-id="element-1200" data-mp="g98jai9nmb5q">
                <span className="text-gray-600" data-id="element-1201" data-mp="3opbgtv71yjgc">
                  {hasMilestoneBonuses ? 'Day 15' : 'Placement (Day 15)'}:{' '}
                  {bonusPopup.day15Reps} × {fmtMoney(pricing.bonusDay15)}
                </span>
                <span className="font-semibold text-gray-900" data-id="element-1202" data-mp="2a0glfea8kws">
                  {fmtMoney(bonusPopup.day15)}
                </span>
              </div>
              {pricing.bonusDay45 > 0 && <div className="flex items-center justify-between gap-4" data-id="element-1203" data-mp="35y25dgdq1km4">
                  <span className="text-gray-600" data-id="element-1204" data-mp="30zqur3flr62u">
                    Day 45: {bonusPopup.day45Reps} ×{' '}
                    {fmtMoney(pricing.bonusDay45)}
                  </span>
                  <span className="font-semibold text-gray-900" data-id="element-1205" data-mp="246j0ycrp5tcj">
                    {fmtMoney(bonusPopup.day45)}
                  </span>
                </div>}
              {pricing.bonusDay90 > 0 && <div className="flex items-center justify-between gap-4" data-id="element-1206" data-mp="2hzih8gzazrl5">
                  <span className="text-gray-600" data-id="element-1207" data-mp="2i1v498jx4s52">
                    Day 90: {bonusPopup.day90Reps} ×{' '}
                    {fmtMoney(pricing.bonusDay90)}
                  </span>
                  <span className="font-semibold text-gray-900" data-id="element-1208" data-mp="2rw3nt6qz1ar3">
                    {fmtMoney(bonusPopup.day90)}
                  </span>
                </div>}
              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-gray-200" data-id="element-1209" data-mp="30xbk5j9xg1ij">
                <span className="font-bold text-navy" data-id="element-1210" data-mp="12kl1amyu2ypu">Total</span>
                <span className="font-bold text-navy" data-id="element-1211" data-mp="16jtt3mabcmir">
                  {fmtMoney(bonusPopup.day15 + bonusPopup.day45 + bonusPopup.day90)}
                </span>
              </div>
              <p className="text-gray-500" data-id="element-1212" data-mp="u9knyr4fcbss">
                {bonusPopup.earnedPeriod ? `Earned in ${bonusPopup.earnedPeriod} and billed in arrears this month. ` : 'Billed in arrears the month after they are earned. '}
                Bonuses are always paid in whole increments, never partially.
              </p>
            </div>
          </div>
        </>}

      {salesPopup && <>
          <div className="fixed inset-0 z-40" onClick={() => setSalesPopup(null)} data-id="element-1213" data-mp="1cz12iicy24s7" />
          <div className="fixed z-50 bg-white text-gray-900 rounded shadow-xl border-2 border-amber p-3 text-xs" style={{
        left: salesPopup.x,
        top: salesPopup.y,
        transform: 'translateX(-50%)',
        maxWidth: 320,
        minWidth: 260
      }} data-id="element-1214" data-mp="140fl4qj7kvt0">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200" data-id="element-1215" data-mp="3q8v59vyy7awf">
              <span className="font-bold text-navy" data-id="element-1216" data-mp="3v5bt0p28jn8z">
                How {salesPopup.period} sales are calculated
              </span>
              <button onClick={() => setSalesPopup(null)} className="text-gray-400 hover:text-gray-700 ml-3 leading-none" aria-label="Close" data-id="element-1217" data-mp="3h983h92b432u">
                ×
              </button>
            </div>
            <div className="space-y-2 text-gray-600" data-id="element-1218" data-mp="3c8kdmqptdsa7">
              {isLeaderSalesMonth(salesPopup.rowIdx) ? <>
                  <div className="flex items-center justify-between gap-4" data-id="element-1219" data-mp="2xh1syfm0f9ki">
                    <span className="font-semibold text-navy" data-id="element-1220" data-mp="32i914fs2earp">
                      Leader rep sales
                    </span>
                    <span className="font-bold text-navy" data-id="element-1221" data-mp="2thb4wjnvb7jo">
                      {salesPopup.totalSales.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-gray-500" data-id="element-1222" data-mp="29vrfkvx8e5jo">
                    {inputs.leaderMonth2Sales !== undefined && inputs.leaderMonth2Sales !== null ? 'This month is set manually. Click the number to change it.' : `The leader sells at 33% of a veteran's rate in the month they're hired: 0.33 × ${inputs.salesVetMonthly} sales/mo. They sell at a full veteran rate for the rest of the first year. In year two the leader stops selling to manage the larger team.`}
                  </p>
                  <p className="text-gray-500" data-id="element-1223" data-mp="32jwzjgggnqig">
                    Click the number in the table to override Month{' '}
                    {leaderMonths} (leader) sales directly.
                  </p>
                </> : <>
              <div className="flex items-start justify-between gap-4" data-id="element-1224" data-mp="3bl4mx3hfv6xr">
                <span data-id="element-1225" data-mp="36mtb4n7l2mcw">
                  New reps ramping: {salesPopup.rampingReps.toFixed(1)} reps at{' '}
                  {rampPcts[0]}% / {rampPcts[1]}% / {rampPcts[2]}% of a
                  veteran&rsquo;s {inputs.salesVetMonthly} sales/mo
                </span>
                <span className="font-semibold text-navy whitespace-nowrap" data-id="element-1226" data-mp="qtrjpqirp1yd">
                  {salesPopup.rampingSales.toFixed(1)}
                </span>
              </div>
              <div className="flex items-start justify-between gap-4" data-id="element-1227" data-mp="vq8hlcfrquon">
                <span data-id="element-1228" data-mp="3v9av0ux4rr1m">
                  Veteran reps: {salesPopup.veterans.toFixed(1)} ×{' '}
                  {inputs.salesVetMonthly} sales/mo
                </span>
                <span className="font-semibold text-navy whitespace-nowrap" data-id="element-1229" data-mp="82rwouuu227q">
                  {salesPopup.veteranSales.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-200" data-id="element-1230" data-mp="3bk0e5dlnf01x">
                <span className="font-semibold text-navy" data-id="element-1231" data-mp="3gik37mvjgryi">Total sales</span>
                <span className="font-bold text-navy" data-id="element-1232" data-mp="3ggh1w6oxrceh">
                  {salesPopup.totalSales.toFixed(1)}
                </span>
              </div>
              <p className="text-gray-500" data-id="element-1233" data-mp="34198t361qjvc">
                <span className="font-semibold text-navy" data-id="element-1234" data-mp="33y50pupgn3ze">
                  Only reps who eventually reach the 90-day mark are counted
                  here.
                </span>{' '}
                Reps who wash out before Day 90 contribute no sales in this
                model, so these counts are smaller than the Launched and Active
                rows above.
              </p>
              <p className="text-gray-500" data-id="element-1235" data-mp="2l8f18uar5u15">
                Ramping reps are in their first 90 days, producing{' '}
                {rampPcts[0]}%, {rampPcts[1]}% and {rampPcts[2]}% of a
                veteran&rsquo;s monthly sales in months 1, 2 and 3 — about{' '}
                {ninetyDayRepSales.toFixed(1)} sales over the period. Veterans
                are past 90 days and taper with your {inputs.pct1y}% one-year
                retention.
              </p>
              {(halfSales || doubleAttrition) && <p className="font-semibold text-orange-600" data-id="element-1236" data-mp="n5uc3ibm268d">
                  A scenario adjustment is applied to these sales.
                </p>}
              </>}
            </div>
          </div>
        </>}
    </div>;
}